/**
 * ENG-7749 - `agt anchor onboard <profile>`: one-time live-view login for an
 * Anchor Browser persistent profile.
 *
 * The operator runs this once per target-site account. It mints a persisting
 * Anchor session bound to the named profile, prints the live-view URL, waits for
 * the human to log in by hand, then ENDS the session - which flushes the
 * authenticated cookies/tokens into the named profile (persist:true). From then
 * on the manager (ENG-7748) mints profile-bound sessions from that name and the
 * agent resumes logged-in with no re-auth.
 *
 * The target-site password is only ever typed into Anchor's remote browser via
 * the live-view frame; it never touches the agent, the manager, or Augmented
 * Team's storage. Our side persists only the non-secret profile NAME.
 *
 * The interactive command wires real deps (the Anchor client, an inquirer
 * confirm, the CLI output helpers) around the pure {@link runAnchorOnboard} core,
 * which is unit-tested with injected deps.
 */

import { confirm } from '@inquirer/prompts';
import { AnchorSessionClient, type AnchorSession, type CreateAnchorSessionInput } from '@augmented/core';
import { success, error, info } from '../lib/output.js';

/** Minimal Anchor client surface the onboarding flow needs (injectable for tests). */
export interface AnchorOnboardClient {
  createSession(input: CreateAnchorSessionInput): Promise<AnchorSession>;
  saveProfileFromSession(name: string, sessionId: string, opts?: { dedicatedStickyIp?: boolean }): Promise<void>;
  endSession(sessionId: string): Promise<void>;
}

export interface AnchorOnboardOptions {
  apiKey?: string;
  apiBase?: string;
  /** Request a dedicated sticky IP (default true - IP stability for LinkedIn etc.). */
  stickyIp?: boolean;
}

export interface AnchorOnboardDeps {
  createClient: (apiKey: string, baseUrl?: string) => AnchorOnboardClient;
  /** Prompt the operator to confirm they've logged in and want to save. */
  confirmSave: (profile: string) => Promise<boolean>;
  out: {
    info: (msg: string) => void;
    success: (msg: string) => void;
    error: (msg: string) => void;
    /** Emit the live-view URL prominently (real impl prints it plainly). */
    url: (url: string) => void;
  };
}

export interface AnchorOnboardResult {
  ok: boolean;
  /** Machine-readable outcome for tests / json callers. */
  reason:
    | 'saved'
    | 'missing-api-key'
    | 'missing-profile'
    | 'mint-failed'
    | 'no-live-view'
    | 'aborted'
    | 'save-failed';
  profile?: string;
}

/** Top-level key names of an unknown payload, for a value-free diagnostic. */
function describeShape(raw: unknown): string {
  if (!raw || typeof raw !== 'object') return typeof raw;
  const keys = Object.keys(raw as Record<string, unknown>);
  return keys.length > 0 ? keys.join(', ') : '(no keys)';
}

/**
 * Pure onboarding flow. Never throws; returns a discriminated result the caller
 * maps to an exit code. Best-effort ends the session on every early exit so a
 * cancelled onboarding doesn't leak a live browser.
 */
export async function runAnchorOnboard(
  profileArg: string,
  opts: AnchorOnboardOptions,
  deps: AnchorOnboardDeps,
): Promise<AnchorOnboardResult> {
  const { out } = deps;
  const profile = (profileArg ?? '').trim();
  const apiKey = (opts.apiKey ?? process.env['ANCHOR_API_KEY'] ?? process.env['ANCHOR_BROWSER_API_KEY'] ?? '').trim();

  if (!profile) {
    out.error('A profile name is required: agt anchor onboard <profile>');
    return { ok: false, reason: 'missing-profile' };
  }
  if (!apiKey) {
    out.error('No Anchor API key. Set ANCHOR_API_KEY (a CLI flag would leak the secret via shell history / process list).');
    return { ok: false, reason: 'missing-api-key' };
  }

  const client = deps.createClient(apiKey, opts.apiBase);

  // A fresh, NON-persisting interactive session (on the sticky IP the profile
  // will use, so the login is captured under that IP). Nothing is saved until
  // the operator confirms - see the save step below.
  let session: AnchorSession;
  try {
    session = await client.createSession({ dedicatedStickyIp: opts.stickyIp !== false });
  } catch (err) {
    out.error(`Failed to start an Anchor session: ${(err as Error).message}`);
    return { ok: false, reason: 'mint-failed' };
  }

  if (!session.liveViewUrl) {
    out.error('Anchor did not return a live-view URL for this session; cannot drive the login by hand.');
    // Value-free shape hint only - the raw response can carry session tokens.
    out.info(`Response shape (keys): ${describeShape(session.raw)}`);
    await endBestEffort(client, session.sessionId);
    return { ok: false, reason: 'no-live-view' };
  }

  out.info(`Onboarding Anchor profile "${profile}".`);
  out.info('1. Open this live-view URL and log into the target site (LinkedIn, etc.) by hand:');
  out.url(session.liveViewUrl);
  out.info('   Your password is typed into Anchor\'s browser only - it never reaches the agent or Augmented Team.');
  out.info('2. Once you are fully logged in, confirm below to save the authenticated profile.');

  let approved = false;
  try {
    approved = await deps.confirmSave(profile);
  } catch {
    approved = false; // e.g. the prompt was interrupted (Ctrl-C)
  }
  if (!approved) {
    // Discard: end the session WITHOUT saving. Nothing is persisted, so an
    // aborted onboarding can never clobber an existing good profile.
    out.info('Aborted - ending the session without saving the profile.');
    await endBestEffort(client, session.sessionId);
    return { ok: false, reason: 'aborted' };
  }

  // Save: snapshot the now-authenticated session into the named profile. This
  // is the explicit save step and must succeed (a subsequent end failure is
  // harmless - the profile is already saved).
  try {
    await client.saveProfileFromSession(profile, session.sessionId, {
      dedicatedStickyIp: opts.stickyIp !== false,
    });
  } catch (err) {
    out.error(`Failed to save the profile: ${(err as Error).message}`);
    await endBestEffort(client, session.sessionId);
    return { ok: false, reason: 'save-failed' };
  }
  await endBestEffort(client, session.sessionId);

  out.success(`Saved Anchor profile "${profile}".`);
  out.info(`Set it on the agent's anchor-browser integration config as profile_name: "${profile}".`);
  out.info('The agent will resume this login on its next session - no re-auth needed.');
  return { ok: true, reason: 'saved', profile };
}

async function endBestEffort(client: AnchorOnboardClient, sessionId: string): Promise<void> {
  try {
    await client.endSession(sessionId);
  } catch {
    // best-effort - the session will hit Anchor's own idle/max-duration expiry.
  }
}

/** Commander action: wires the real deps around {@link runAnchorOnboard}. */
export async function anchorOnboardCommand(profile: string, options: AnchorOnboardOptions): Promise<void> {
  const result = await runAnchorOnboard(profile, options, {
    createClient: (apiKey, baseUrl) => new AnchorSessionClient({ apiKey, ...(baseUrl ? { baseUrl } : {}) }),
    confirmSave: (p) =>
      confirm({ message: `Finished logging in as "${p}"? Save the profile now?`, default: true }),
    out: {
      info,
      success,
      error,
      // The URL is data the operator must copy, so print it plainly on its own line.
      url: (u) => console.log(`\n  ${u}\n`),
    },
  });
  if (!result.ok) process.exitCode = 1;
}
